import React, { useEffect, useMemo, useState } from 'react';

import moment from 'moment';
import { useDispatch } from 'react-redux';

import { usersChangeStatus, userUnlock } from 'api/endpoints/user-management';
import Table from 'components/Table';
import { ENUM_USER_STATUS } from 'constants/enum';
import { FORMAT_TIME } from 'constants/index';
import { TEXT_COMMON, TEXT_USER_LIST } from 'constants/string/texts';
import { toastError, toastSuccess } from 'lib/toast';
import Profile from 'pages/Users/UserProfile/Profile';
import { clearModal, setLoading, setModal } from 'store/actions/common';
import { convertStatusUser, getCodeJapaneseGovernmentsWith } from 'utils/helper';

const ReportListTable = (props) => {
  const { data, setSort, reloadData, sort, page, keyword, isFilter, onPageChange } = props;
  const dispatch = useDispatch();
  const [selectedRows, setSelectedRows] = useState([{ page: page, selected: [] }]);
  const [showSettingUser, setShowSettingUser] = useState({ isOpen: false, id: null });

  const onClickChangeStatus = async (id, isApprove = true) => {
    const confirmApproveRejectUsers = async () => {
      try {
        dispatch(setLoading({ isOpen: true }));
        const dataChangeStatus = {
          status: isApprove ? ENUM_USER_STATUS.APPROVED : ENUM_USER_STATUS.REJECTED,
          user_ids: [id],
        };
        const result = await usersChangeStatus(dataChangeStatus);
        toastSuccess(result?.message);
      } catch (error) {
        toastError(error.message);
      } finally {
        dispatch(setLoading({ isOpen: false }));
        if (onPageChange) {
          const result = await onPageChange(1);
          if (!result) {
            if (reloadData) {
              await reloadData();
            }
          }
        } else if (reloadData) {
          await reloadData();
        }
        window.scrollTo(0, 0);
      }
    };

    dispatch(
      setModal({
        isOpen: true,
        type: 'delete',
        title: isApprove ? TEXT_USER_LIST.APPROVE_CONFIRM_TEXT : TEXT_USER_LIST.REJECT_CONFIRM_TEXT,
        cancelText: TEXT_COMMON.CANCEL_TEXT,
        confirmText: TEXT_COMMON.YES,
        onCancel: () => dispatch(clearModal()),
        onConfirm: () => {
          dispatch(clearModal());
          confirmApproveRejectUsers();
        },
        classWrapModal: '!max-w-104',
      })
    );
  };

  const onClickUnlockAccount = async (id) => {
    const handleUnlockUser = async () => {
      try {
        dispatch(setLoading({ isOpen: true }));
        const result = await userUnlock(id);
        toastSuccess(result?.message);
      } catch (error) {
        toastError(error.message);
      } finally {
        dispatch(setLoading({ isOpen: false }));
        if (onPageChange) {
          const result = await onPageChange(1);
          if (!result) {
            if (reloadData) {
              await reloadData();
            }
          }
        } else if (reloadData) {
          await reloadData();
        }
        window.scrollTo(0, 0);
      }
    };

    dispatch(
      setModal({
        isOpen: true,
        type: 'delete',
        title: TEXT_USER_LIST.UNLOCK_USER_CONFIRM,
        cancelText: TEXT_COMMON.CANCEL_TEXT,
        confirmText: TEXT_COMMON.YES,
        onCancel: () => dispatch(clearModal()),
        onConfirm: () => {
          dispatch(clearModal());
          handleUnlockUser();
        },
        classWrapModal: '!max-w-104',
      })
    );
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: '-',
        header: () => <span>&nbsp;</span>,
        cell: ({ row }) =>
          row?.original?.is_blocked ? (
            <div className="cursor-pointer" onClick={() => onClickUnlockAccount(row?.original?.id)}>
              <img src={require('assets/resources/UserLocked.svg').default} alt="user locked" width={20} height={16} />
            </div>
          ) : (
            <div>
              <img src={require('assets/resources/UserUnlock.svg').default} alt="user unlock" width={20} height={16} />
            </div>
          ),
        size: 48,
        enableSorting: false,
      },
      {
        accessorKey: 'email',
        header: ({ table }) => {
          setSort(table.getState().sorting);
          return <span>{TEXT_USER_LIST.EMAIL_TEXT}</span>;
        },
        cell: ({ row, getValue }) => {
          return (
            <div className="flex-between-center text-sm gap-5">
              <span className={`break-all ${row?.original?.is_blocked && '!text-gray-651 !cursor-not-allowed'}`}>{getValue()}</span>
            </div>
          );
        },
        size: 335,
        sortingFn: () => {},
      },
      {
        accessorKey: 'status',
        size: 144,
        header: () => <span>{TEXT_USER_LIST.STATUS_TEXT}</span>,
        cell: ({ row }) => {
          return (
            <>
              <span
                className={`
                  text-xs 
                  rounded-sm 
                  flex-middle-center 
                  border 
                  border-solid 
                  min-w-13 
                  w-19 
                  min-h-5.75 
                  ${row?.original?.is_blocked && '!text-gray-651 !bg-white-714/50 !border-gray-651 !cursor-not-allowed'}
                  ${convertStatusUser(row?.original?.status)?.class}
                `}
              >
                {convertStatusUser(row.original.status).name}
              </span>
            </>
          );
        },
        sortingFn: () => {},
      },
      {
        accessorKey: 'municipality',
        size: 144,
        header: () => <span>{TEXT_USER_LIST.LOCAL_GOVERNMENT}</span>,
        cell: ({ row, getValue }) => {
          return (
            <div className="flex-between-center text-sm gap-5">
              <span className={`break-all ${row?.original?.is_blocked && '!text-gray-651 !cursor-not-allowed'}`}>
                {getCodeJapaneseGovernmentsWith(getValue(), false)}
              </span>
            </div>
          );
        },
        sortingFn: () => {},
      },
      {
        accessorKey: 'updated_at',
        size: 144,
        header: () => <span>{TEXT_USER_LIST.EDITING_DATE}</span>,
        cell: ({ row }) => (
          <span className={row?.original?.is_blocked && '!text-gray-651 !cursor-not-allowed'}>
            {moment(row.original.updated_at).format(FORMAT_TIME.DATE)}
          </span>
        ),
        sortingFn: () => {},
      },
      {
        accessorKey: '-',
        header: () => <span></span>,
        size: 80,
        cell: ({ row }) => (
          <div className="flex-between-center text-sm">
            <span
              className={`text-center 
                rounded-sm 
                py-0.5 
                whitespace-nowrap 
                ${
                  [ENUM_USER_STATUS.REJECTED, ENUM_USER_STATUS.NEW].includes(row?.original?.status) && !row?.original?.is_blocked
                    ? 'text-primary-346 cursor-pointer'
                    : 'text-gray-651 cursor-not-allowed'
                }
                `}
              onClick={() => {
                if ([ENUM_USER_STATUS.REJECTED, ENUM_USER_STATUS.NEW].includes(row?.original?.status) && !row?.original?.is_blocked) {
                  onClickChangeStatus(row?.original?.id, true);
                }
              }}
            >
              {TEXT_USER_LIST.APPROVE}
            </span>
          </div>
        ),
        enableSorting: false,
      },
      {
        accessorKey: '-',
        header: () => <span></span>,
        size: 80,
        cell: ({ row }) => (
          <div className="flex-between-center text-sm">
            <span
              className={`
                text-center 
                rounded-sm 
                py-0.5 
                whitespace-nowrap 
                ${
                  [ENUM_USER_STATUS.APPROVED, ENUM_USER_STATUS.NEW].includes(row?.original?.status) && !row?.original?.is_blocked
                    ? 'text-danger-229 cursor-pointer'
                    : 'text-gray-651 cursor-not-allowed'
                }
              `}
              onClick={() => {
                if ([ENUM_USER_STATUS.APPROVED, ENUM_USER_STATUS.NEW].includes(row?.original?.status) && !row?.original?.is_blocked) {
                  onClickChangeStatus(row?.original?.id, false);
                }
              }}
            >
              {TEXT_USER_LIST.REJECT}
            </span>
          </div>
        ),
        enableSorting: false,
      },
      {
        accessorKey: '-',
        header: () => <span></span>,
        size: 80,
        cell: ({ row }) => (
          <div className="flex-between-center text-sm">
            <span
              className={`text-center 
                rounded-sm 
                py-0.5 
                cursor-pointer 
                whitespace-nowrap 
                text-primary-346 
                ${row?.original?.is_blocked && '!text-gray-651 !cursor-not-allowed'}
              `}
              onClick={() => {
                if (!row?.original?.is_blocked) {
                  setShowSettingUser({ isOpen: true, id: row?.original?.id });
                }
              }}
            >
              {TEXT_USER_LIST.VIEW}
            </span>
          </div>
        ),
        enableSorting: false,
      },
    ],
    [selectedRows, page]
  );

  useEffect(() => {
    setSelectedRows([]);
  }, [sort, keyword]);

  return (
    <>
      <Table columns={columns} data={data} isFilter={isFilter} isUserList={true} />
      {showSettingUser?.isOpen && <Profile id={showSettingUser.id} close={() => setShowSettingUser({ isOpen: false, id: null })} {...props} />}
    </>
  );
};

export default React.memo(ReportListTable);
